'use client';

import { useTranslations } from 'next-intl';
import { useState, useRef, useEffect } from 'react';
import { CustomImage as Image } from '../..';
import { useFormatPrice } from 'hooks/pricing/useFormattedPrice';
import { useModal } from 'components/shared/Modal';
import { DiscountPill } from 'components/shared/DiscountPill';
import { CloseCircle } from 'assets/icons';
import styles from './StackingSection.module.css';

const cx = (...classes: (string | undefined)[]) =>
  classes.filter(Boolean).join(' ');
import {
  DUO_SAVE_PERCENTAGE,
  RingStackType,
  StackVariant,
  STACK_OPTIONS,
  STACK_SUB_VARIANTS,
  StackOptionConfig,
  StackSubVariantConfig,
} from 'content/products/ring/stacking/constants';
import {
  AirRingColors,
  getAvailableAirRingColors,
  getRingAirColorNameImages,
} from 'content/products/ring';

const RING_COLOR_TO_STACK: Record<AirRingColors, RingStackType> = {
  [AirRingColors.ASTER_BLACK]: RingStackType.SILVER_HALO,
  [AirRingColors.MATTE_GREY]: RingStackType.GOLD_HALO,
  [AirRingColors.RAW_TITANIUM]: RingStackType.SILVER_HALO,
  [AirRingColors.SPACE_SILVER]: RingStackType.SILVER_HALO,
  [AirRingColors.BIONIC_GOLD]: RingStackType.GOLD_HALO,
  [AirRingColors.ROSE_GOLD]: RingStackType.GOLD_HALO,
};

const RING_COLOR_CSS: Record<AirRingColors, string> = {
  [AirRingColors.RAW_TITANIUM]:
    'linear-gradient(135deg, #D9D9D9 0%, #9B9B9B 100%)',
  [AirRingColors.BIONIC_GOLD]:
    'linear-gradient(135deg, #FFEBBC 0%, #93775A 100%)',
  [AirRingColors.ASTER_BLACK]:
    'linear-gradient(135deg, #666666 0%, #000000 100%)',
  [AirRingColors.MATTE_GREY]:
    'linear-gradient(135deg, #A6A6A6 0%, #5E5E5E 100%)',
  [AirRingColors.SPACE_SILVER]:
    'linear-gradient(135deg, #D9D9D9 0%, #9B9B9B 100%)',
  [AirRingColors.ROSE_GOLD]:
    'linear-gradient(135deg, #F6CABE 0%, #EABA95 100%)',
};

type StackingSectionProps = {
  selectedStack: RingStackType | null;
  onSelectStack: (stack: RingStackType | null) => void;
  selectedVariant: StackVariant | null;
  onSelectVariant: (variant: StackVariant) => void;
  prices: Partial<Record<RingStackType, number>>;
  duoPrices: Partial<Record<RingStackType, number>>;
  originalPrices?: Partial<Record<RingStackType, number>>;
  originalDuoPrices?: Partial<Record<RingStackType, number>>;
  discountPercentage?: number;
  currencySymbol: React.ReactNode;
  isMobile: boolean;
  onOpenImages?: () => void;
  ringColor?: AirRingColors;
  soldOutStacks?: RingStackType[];
};

export const StackingSelectionContent: React.FC<StackingSectionProps> = ({
  selectedStack,
  onSelectStack,
  selectedVariant,
  onSelectVariant,
  prices,
  duoPrices,
  originalPrices,
  originalDuoPrices,
  discountPercentage,
  currencySymbol,
  onOpenImages,
  ringColor,
  soldOutStacks,
}) => {
  const t = useTranslations('ring');
  const tCommon = useTranslations('common');
  const formatPriceWithComma = useFormatPrice();

  return (
    <div className={styles.stackingContainer}>
      <div className="stacking-header">
        <div className="stacking-thumbnail">
          <Image
            src="web_v2/ring-buy/stacking_thumbnail_updated.png/w_200"
            alt={tCommon('stackingSection.image.alt.ringStack')}
            width={88}
            height={88}
            style={{ borderRadius: '12px' }}
          />
        </div>
        <div className="stacking-header-text">
          <p className="stacking-description">
            {t('ring.productSelection.stacking.headerDescription')}
          </p>
          {onOpenImages && (
            <button className="images-badge" onClick={onOpenImages}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
              >
                <path
                  d="M12.8333 5.83333H12.2506C11.438 5.83333 11.0317 5.83333 10.6895 5.8685C7.52736 6.19359 5.02749 8.69345 4.70241 11.8556C4.69035 11.9729 4.68243 12.0977 4.67722 12.2437M12.8333 5.83333C12.8339 6.01549 12.8339 6.20951 12.8339 6.41667V7.58333C12.8339 9.21682 12.8339 10.0336 12.516 10.6575C12.2364 11.2063 11.7902 11.6525 11.2414 11.9321C10.6175 12.25 9.80072 12.25 8.16724 12.25H5.8339C5.39291 12.25 5.01144 12.25 4.67722 12.2437M12.8333 5.83333C12.8288 4.57913 12.7936 3.88731 12.516 3.34253C12.2364 2.79372 11.7902 2.34753 11.2414 2.0679C10.6175 1.75 9.80072 1.75 8.16724 1.75H5.8339C4.20042 1.75 3.38367 1.75 2.75976 2.0679C2.21096 2.34753 1.76476 2.79372 1.48513 3.34253C1.16724 3.96644 1.16724 4.78318 1.16724 6.41667V7.58333C1.16724 9.21682 1.16724 10.0336 1.48513 10.6575C1.76476 11.2063 2.21096 11.6525 2.75976 11.9321C3.21524 12.1642 3.77348 12.2268 4.67722 12.2437M4.37557 5.54167C4.0534 5.54167 3.79224 5.2805 3.79224 4.95833C3.79224 4.63617 4.0534 4.375 4.37557 4.375C4.69774 4.375 4.9589 4.63617 4.9589 4.95833C4.9589 5.2805 4.69774 5.54167 4.37557 5.54167Z"
                  stroke="#111111"
                  strokeWidth="1.14287"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {t('ring.productSelection.stacking.imagesBadge')}
            </button>
          )}
        </div>
      </div>

      <div className="stacking-options">
        {STACK_OPTIONS.map((option: StackOptionConfig) => {
          const isSoldOut = soldOutStacks?.includes(option.id) ?? false;
          const isSelected = !isSoldOut && selectedStack === option.id;
          const price = prices[option.id] ?? 0;
          const duoPrice = duoPrices[option.id] ?? 0;
          const originalPrice = originalPrices?.[option.id] ?? 0;
          const originalDuoPrice = originalDuoPrices?.[option.id] ?? 0;

          const isSuggestedForColor =
            ringColor && RING_COLOR_TO_STACK[ringColor] === option.id;
          const hasTag =
            isSoldOut ||
            (ringColor ? isSuggestedForColor : option.isRecommended);

          return (
            <div key={option.id} className="stacking-card-group">
              <button
                type="button"
                className={`stacking-card ${isSelected ? 'active' : ''} ${
                  hasTag ? 'has-tag' : ''
                } ${isSoldOut ? 'sold-out' : ''}`}
                data-testid={
                  option.id === RingStackType.SILVER_HALO
                    ? 'ring-stacking-silver'
                    : 'ring-stacking-gold'
                }
                onClick={() =>
                  !isSoldOut && onSelectStack(isSelected ? null : option.id)
                }
                style={isSoldOut ? { cursor: 'default' } : undefined}
              >
                {isSoldOut ? (
                  <span className="corner-tag sold-out-tag">
                    {tCommon('stackingSection.text.soldOut')}
                  </span>
                ) : (ringColor ? isSuggestedForColor : option.isRecommended) ? (
                  <span className="corner-tag">
                    {t('ring.productSelection.stacking.cornerTag')}
                  </span>
                ) : null}

                <div className="card-content">
                  <div className="card-title-row">
                    <span className="card-title">{t(option.titleKey)}</span>
                    {!isSoldOut && (
                      <span className="card-price">
                        {originalPrice > price && (
                          <span className="card-price-striked">
                            {currencySymbol}
                            {formatPriceWithComma(originalPrice)}
                          </span>
                        )}
                        {currencySymbol}
                        {formatPriceWithComma(price)}
                      </span>
                    )}
                  </div>
                  <p className="card-description">{t(option.descriptionKey)}</p>
                  {!isSoldOut && discountPercentage ? (
                    <DiscountPill>
                      {t('ring.productSelection.stacking.discountTag', {
                        percent: discountPercentage,
                      })}
                    </DiscountPill>
                  ) : null}
                </div>
              </button>

              <div
                className={`sub-variant-wrapper ${
                  isSelected ? 'expanded' : ''
                }`}
              >
                <div className="sub-variant-inner">
                  <div className="sub-variant-panel">
                    <p className="sub-variant-label">
                      {t('ring.productSelection.stacking.selectYourStack')}
                    </p>
                    <div className="sub-variant-options">
                      {STACK_SUB_VARIANTS.map(
                        (subVariant: StackSubVariantConfig) => {
                          const isSubSelected =
                            selectedVariant === subVariant.id;

                          return (
                            <button
                              key={subVariant.id}
                              type="button"
                              className={`sub-variant-card ${
                                isSubSelected ? 'active' : ''
                              }`}
                              data-testid={
                                subVariant.id === StackVariant.SINGLE
                                  ? 'ring-stacking-single'
                                  : 'ring-stacking-duo'
                              }
                              onClick={() => onSelectVariant(subVariant.id)}
                            >
                              <div className="sub-variant-thumb">
                                <Image
                                  src={`${
                                    subVariant.thumbnails[
                                      option.id as
                                        | RingStackType.SILVER_HALO
                                        | RingStackType.GOLD_HALO
                                    ]
                                  }/w_200`}
                                  alt={t(subVariant.titleKey)}
                                  width={72}
                                  height={72}
                                />
                              </div>
                              <div className="sub-variant-content">
                                <span className="sub-variant-title">
                                  {subVariant.showPriceInTitle ? (
                                    <>
                                      {t(subVariant.titleKey)} at{' '}
                                      {originalDuoPrice > duoPrice && (
                                        <span className="card-price-striked">
                                          {currencySymbol}
                                          {formatPriceWithComma(
                                            originalDuoPrice,
                                          )}
                                        </span>
                                      )}
                                      {currencySymbol}
                                      {formatPriceWithComma(duoPrice)}
                                    </>
                                  ) : (
                                    t(subVariant.titleKey)
                                  )}
                                </span>
                                {subVariant.isRecommended && (
                                  <span className="duo-save-tag">
                                    {discountPercentage
                                      ? t(
                                          'ring.productSelection.stacking.duoSaveTagWithDiscount',
                                          {
                                            percent: discountPercentage,
                                            duoPercent: DUO_SAVE_PERCENTAGE,
                                          },
                                        )
                                      : t(
                                          'ring.productSelection.stacking.duoSaveTag',
                                        )}
                                  </span>
                                )}
                              </div>
                            </button>
                          );
                        },
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        <button
          className={`stacking-card no-stack ${
            selectedStack === RingStackType.NONE ? 'active' : ''
          }`}
          data-testid="ring-stacking-none"
          onClick={() =>
            onSelectStack(
              selectedStack === RingStackType.NONE ? null : RingStackType.NONE,
            )
          }
        >
          <span className="no-stack-text">
            {t('ring.productSelection.stacking.noStack')}
          </span>
        </button>

        <p className="stacking-shipping-note" style={{ marginTop: '4px' }}>
          {/* <InfoIcon
            style={{ width: '14px', height: 'auto', flexShrink: 0 }}
            fill="rgba(0, 0, 0, 0.5)"
          /> */}
          * {t('ring.productSelection.stacking.shippingNote')}
        </p>
      </div>
    </div>
  );
};


// --- Stacking Images Modal ---

const STACKING_IMAGE_TABS = [
  {
    id: RingStackType.SILVER_HALO,
    titleKey: 'ring.productSelection.stacking.silverHalo.title',
  },
  {
    id: RingStackType.GOLD_HALO,
    titleKey: 'ring.productSelection.stacking.goldHalo.title',
  },
];

const STACKING_IMAGES_COUNT = 4;

export const StackingImagesModalContent = ({
  className,
  selectedColor,
  hideTabs,
  initialTab,
}: {
  className?: string;
  selectedColor?: AirRingColors;
  hideTabs?: boolean;
  initialTab?: RingStackType;
}) => {
    const t = useTranslations('ring');
    const tCommon = useTranslations('common');
    const { hide: hideModal } = useModal();
    const RingAirColorNameImages = getRingAirColorNameImages(t);
    const availableColors = getAvailableAirRingColors();
    const [activeTab, setActiveTab] = useState<RingStackType>(
      initialTab ?? RingStackType.SILVER_HALO,
    );
    const [activeColor, setActiveColor] = useState<AirRingColors>(
      selectedColor && availableColors.includes(selectedColor)
        ? selectedColor
        : (availableColors[0] ?? AirRingColors.ASTER_BLACK),
    );

    const showTabs =
      activeColor === AirRingColors.ASTER_BLACK ||
      activeColor === AirRingColors.MATTE_GREY;
    const [colorPickerOpen, setColorPickerOpen] = useState(false);
    const [pendingColor, setPendingColor] = useState<AirRingColors | null>(
      null,
    );
    const [pendingTab, setPendingTab] = useState<RingStackType | null>(null);
    const [animClass, setAnimClass] = useState('');
    const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
    const pickerRef = useRef<HTMLDivElement>(null);

    const handleColorChange = (color: AirRingColors) => {
      if (color === activeColor) {
        setColorPickerOpen(false);
        return;
      }
      setPendingColor(color);
      setAnimClass('fade-out');
      setColorPickerOpen(false);
    };

    const handleTabChange = (tab: RingStackType) => {
      if (tab === activeTab) return;
      setPendingTab(tab);
      setAnimClass('fade-out');
    };

    const handleImgAnimEnd = () => {
      if (animClass === 'fade-out') {
        if (pendingColor) {
          setActiveColor(pendingColor);
          setPendingColor(null);
        }
        if (pendingTab) {
          setActiveTab(pendingTab);
          setPendingTab(null);
        }
        setAnimClass('fade-in');
      } else if (animClass === 'fade-in') {
        setAnimClass('');
      }
    };

    useEffect(() => {
      const idx = STACKING_IMAGE_TABS.findIndex((tab) => tab.id === activeTab);
      if (tabRefs.current[idx]) {
        tabRefs.current[idx]?.scrollIntoView({
          behavior: 'smooth',
          inline: 'center',
          block: 'nearest',
        });
      }
    }, [activeTab]);

    // Close picker on outside click
    useEffect(() => {
      if (!colorPickerOpen) return;
      const handleClick = (e: MouseEvent) => {
        if (
          pickerRef.current &&
          !pickerRef.current.contains(e.target as Node)
        ) {
          setColorPickerOpen(false);
        }
      };
      document.addEventListener('mousedown', handleClick);
      return () => document.removeEventListener('mousedown', handleClick);
    }, [colorPickerOpen]);

    const getColorDir = (color: AirRingColors) => {
      switch (color) {
        case AirRingColors.ASTER_BLACK:
          return 'aster_black';
        case AirRingColors.MATTE_GREY:
          return 'matte_grey';
        case AirRingColors.BIONIC_GOLD:
          return 'bionic_gold';
        case AirRingColors.SPACE_SILVER:
          return 'space_silver';
        case AirRingColors.RAW_TITANIUM:
          return 'raw_titanium';
        case AirRingColors.ROSE_GOLD:
          return 'brushed_rose_gold';
        default:
          return 'raw_titanium';
      }
    };

    const getImageSrc = (index: number) => {
      const colorDir = getColorDir(activeColor);
      if (
        activeColor === AirRingColors.ASTER_BLACK ||
        activeColor === AirRingColors.MATTE_GREY
      ) {
        const stackDir =
          activeTab === RingStackType.GOLD_HALO ? 'gold_stack' : 'silver_stack';
        return `web_v2/ring-buy/stacking-modal/${colorDir}/${stackDir}/${
          index + 1
        }_resp.png?v=2`;
      }
      return `web_v2/ring-buy/stacking-modal/${colorDir}/${
        index + 1
      }_resp.png?v=2`;
    };

    return (
      <div className={cx(styles.stackingImagesModal, className)}>
        <button
          className="stacking-modal-close"
          onClick={hideModal}
          aria-label={tCommon('stackingSection.image.alt.close')}
        >
          <CloseCircle />
        </button>
        <h2 style={{ marginBottom: '4px' }}>
          {t('ring.productSelection.stacking.modalTitle')}
          <br className="mobile-break" />{' '}
          {t('ring.productSelection.stacking.modalTitle2')}
        </h2>
        <p className="modal-description" style={{ marginBottom: '20px' }}>
          {t('ring.productSelection.stacking.modalDescription')}
        </p>
        {showTabs && !hideTabs && (
          <div className="btn-container-wrapper u-hide-scrollbar">
            <div className="btn-container">
              {STACKING_IMAGE_TABS.map((tab, index) => (
                <button
                  key={tab.id}
                  ref={(el) => {
                    tabRefs.current[index] = el;
                  }}
                  onClick={() => handleTabChange(tab.id)}
                  className={activeTab === tab.id ? 'active' : undefined}
                >
                  {t(tab.titleKey)}
                </button>
              ))}
            </div>
          </div>
        )}
        <div
          className={`img-container ${animClass}`}
          onAnimationEnd={handleImgAnimEnd}
        >
          {Array.from({ length: STACKING_IMAGES_COUNT }, (_, i) => (
            <Image
              key={`${activeTab}-${activeColor}-${i}`}
              src={getImageSrc(i)}
              alt={t(
                STACKING_IMAGE_TABS.find((tab) => tab.id === activeTab)
                  ?.titleKey ?? '',
              )}
              width="400"
              height="400"
            />
          ))}
        </div>
        <div className="material-info">
          <hr className="material-divider" />
          {(() => {
            const isSilverMaterial = showTabs
              ? activeTab === RingStackType.SILVER_HALO
              : activeColor === AirRingColors.SPACE_SILVER ||
                activeColor === AirRingColors.RAW_TITANIUM;
            const materialKey = isSilverMaterial ? 'silverHalo' : 'goldHalo';
            return (
              <>
                <p className="material-text">
                  {t(`ring.productSelection.stacking.${materialKey}Material`)}
                </p>
                <p className="material-text">
                  {t(`ring.productSelection.stacking.${materialKey}Material2`)}
                </p>
                <p className="material-text">
                  {t(
                    `ring.productSelection.stacking.${materialKey}MaterialFooter`,
                  )}
                </p>
              </>
            );
          })()}
          <Image
            src="web_v2/ring-buy/stacking_stamp.png/w_200"
            alt={tCommon('stackingSection.image.alt.certificationStamp')}
            width={100}
            height={100}
            className="stamp-image"
          />
        </div>
        <div className="color-picker-floating" ref={pickerRef}>
          {colorPickerOpen && (
            <div className="color-picker-popover">
              {availableColors.map((color) => (
                <button
                  key={color}
                  className={`color-option ${
                    activeColor === color ? 'active' : ''
                  }`}
                  onClick={() => handleColorChange(color)}
                >
                  <span
                    className="color-circle"
                    style={{ background: RING_COLOR_CSS[color] }}
                  />
                  <span>{RingAirColorNameImages[color]?.name}</span>
                </button>
              ))}
            </div>
          )}
          <button
            className="change-colour-btn"
            onClick={() => setColorPickerOpen((prev) => !prev)}
          >
            <span
              className="color-circle"
              style={{ background: RING_COLOR_CSS[activeColor] }}
            />
            {t('ring.productSelection.stacking.changeColour')}
          </button>
        </div>
      </div>
    );
};
